import React from "react";
import { Brand, Button, ConfirmDialog, Panel, Pill, formatDateTime, formatRelative } from "@ozpanel/ui";
import { api, TokenSignIn, useRecoveryCredential } from "./recoveryCredential";
import { PhasedSteps } from "./nodes/PhasedSteps";

// InstallScreen — this node's install, step by step.
//
// An install runs in two processes: `ozpanel bootstrap` runs the steps that need no panel (before the
// panel and its database exist), then the panel runs the rest. Both write the node's install record
// (installstate), which is what this screen reads — so it shows the whole install, including one that
// failed before the database existed. No login exists on a new node until the install creates one, so
// it signs in like recovery mode: the node's recovery token (the install creates it) or a recovery grant.
// A failed install is resumed from here: the same `ozpanel bootstrap`, continuing from the failed step.

type Step = {
  name: string;
  phase: number;
  label: string;
  status: string;
  detail?: string;
  started_at?: string;
  finished_at?: string;
};

/** GET /install/state (modules/install). */
type InstallState = {
  recorded: boolean;
  running: boolean;
  resume_running: boolean;
  log_file: string;
  note?: string;
  /** Where the record came from: this node's file, or — once cleared after archiving — the database. */
  source?: string;
  install?: {
    node: string;
    kind: string;
    stage: string;
    job_id?: string;
    error?: string;
    started_at: string;
    updated_at: string;
    finished_at?: string;
    steps: Step[];
  };
};

const STAGE: Record<string, { label: string; tone: "ok" | "warn" | "crit" | "accent" | "neutral" }> = {
  base: { label: "installing the base system and services", tone: "accent" },
  schema: { label: "creating the panel's database schema", tone: "accent" },
  panel: { label: "the panel is running the remaining steps", tone: "accent" },
  paused: { label: "paused at the handover", tone: "warn" },
  complete: { label: "complete", tone: "ok" },
  failed: { label: "failed", tone: "crit" },
};

export function InstallScreen() {
  const { token, signIn, signOut } = useRecoveryCredential();
  const [data, setData] = React.useState<InstallState | null>(null);
  const [log, setLog] = React.useState("");
  const [err, setErr] = React.useState<string | null>(null);
  const [confirm, setConfirm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const logRef = React.useRef<HTMLPreElement>(null);

  const inst = data?.install;
  const active = !!(data?.running || data?.resume_running || (inst && ["base", "schema", "panel"].includes(inst.stage)));

  const load = React.useCallback(async () => {
    if (!token) return;
    try {
      const [st, lg] = await Promise.all([api<InstallState>("/install/state"), api<{ log: string }>("/install/log?lines=600")]);
      setData(st);
      setLog(lg.log ?? "");
      setErr(null);
    } catch (e) {
      const x = e as Error & { status?: number };
      if (x.status === 401) {
        signOut();
      }
      // The panel restarts during an install (the bootstrap restarts it): a failed poll then is
      // expected and the next one recovers, so it is shown, not treated as the end.
      setErr(x.message);
    }
  }, [token]);

  React.useEffect(() => {
    void load();
    if (!token) return;
    const t = window.setInterval(() => void load(), active ? 3000 : 15000);
    return () => window.clearInterval(t);
  }, [load, token, active]);

  React.useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [log]);

  async function resume() {
    setBusy(true);
    try {
      await api("/install/resume", { method: "POST", body: "{}" });
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <TokenSignIn
        title="Install progress"
        subtitle="This node's install, step by step."
        error={err}
        onSignIn={(t) => {
          setErr(null);
          signIn(t);
        }}
      />
    );
  }

  const steps: Step[] = inst?.steps ?? [];
  const done = steps.filter((s) => s.status === "done" || s.status === "skipped").length;
  const current = steps.find((s) => s.status === "running");
  const failed = steps.find((s) => s.status === "failed");
  const stage = STAGE[inst?.stage ?? ""] ?? { label: inst?.stage ?? "unknown", tone: "neutral" as const };
  const pct = steps.length ? Math.round((done / steps.length) * 100) : 0;

  return (
    <main className="mx-auto max-w-[1100px] p-4 sm:p-6">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Brand />
          <div>
            <h1 className="text-[18px] font-[680]">Install · {inst?.node ?? "this node"}</h1>
            <p className="text-[12.5px] text-muted">
              {inst?.kind ?? ""}
              {inst?.started_at ? ` · started ${formatDateTime(inst.started_at)}` : ""}
              {inst?.updated_at ? ` · last change ${formatRelative(inst.updated_at)}` : ""}
            </p>
            {data?.source && <p className="text-[11.5px] text-faint">from {data.source}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => void load()}>
            Refresh
          </Button>
          <Button variant="ghost" onClick={signOut}>
            Leave
          </Button>
        </div>
      </header>

      {err && (
        <p className="mb-4 rounded-lg border border-line bg-warnsoft px-3 py-2.5 text-[13px] text-warn">
          {err}
          {active ? " — the panel restarts during an install; this screen keeps trying." : ""}
        </p>
      )}

      {data && !data.recorded && (
        <Panel className="mb-4 p-4 text-[13px] text-muted">{data.note}</Panel>
      )}

      {inst && (
        <Panel className="mb-4 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Pill tone={stage.tone}>{stage.label}</Pill>
              <span className="text-[13px] text-muted">
                {done} of {steps.length} steps
                {current ? ` · now: ${current.label || current.name}` : ""}
              </span>
            </div>
            {inst.stage === "complete" && (
              <a href={import.meta.env.BASE_URL} className="text-[13px] text-accent hover:underline">
                Sign in to the panel →
              </a>
            )}
            {inst.stage === "failed" && (
              <Button variant="accent" disabled={busy || data?.running || data?.resume_running} onClick={() => setConfirm(true)}>
                Resume from {failed ? failed.name : "the failed step"}…
              </Button>
            )}
          </div>
          <div className="mt-3 h-1.5 overflow-hidden rounded bg-bg2">
            <div
              className={`h-full ${inst.stage === "failed" ? "bg-crit" : inst.stage === "complete" ? "bg-ok" : "bg-accent"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          {inst.stage === "failed" && inst.error && (
            <pre className="mt-3 max-h-[200px] overflow-auto rounded border border-line bg-critsoft p-3 font-mono text-[12px] whitespace-pre-wrap text-crit">
              {inst.error}
            </pre>
          )}
          {inst.stage === "paused" && (
            <p className="mt-3 text-[12.5px] text-muted">
              Stopped at the handover on purpose (a floating-IP install). The install script continues it; nothing to do here.
            </p>
          )}
        </Panel>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <PhasedSteps steps={steps} />
        <Panel className="flex min-h-[300px] flex-col p-3">
          <h2 className="mb-1.5 text-[12px] font-medium tracking-wide text-muted uppercase">
            Install log {active && <span className="ml-1 text-accent normal-case">· live</span>}
          </h2>
          <pre
            ref={logRef}
            className="max-h-[70vh] flex-1 overflow-auto rounded bg-bg p-2 font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap"
          >
            {log || "No output recorded yet."}
          </pre>
          <p className="mt-1.5 text-[11.5px] text-faint">{data?.log_file}</p>
        </Panel>
      </div>

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Resume the install?"
        body="Runs the install again on this node, continuing from the step that failed — every step before it is kept, and every step is safe to run twice. Fix the cause shown above first; resuming an unfixed failure fails the same way."
        confirmLabel="Resume"
        onConfirm={() => {
          setConfirm(false);
          void resume();
        }}
      />
    </main>
  );
}
